import axiosNextGen from '../../../config/axios-nextgen';
import Globals from '../../../config/globals';

/**
 * Widget Data API Service
 * Handles fetching widget data and counts
 */

/**
 * Get count for a specific widget
 * @param {string} widgetKey - The widget key identifier
 * @returns {Promise<number>} The count
 */
export async function getWidgetCount(widgetKey, selectedFacilities = []) {
    const response = await axiosNextGen.get(`/api/widget-data/${widgetKey}`, {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data.count;
}

/**
 * Get full data for a specific widget
 * @template T
 * @param {string} widgetKey - The widget key identifier
 * @returns {Promise<{count: number, data: T[]}>} The widget data response
 */
export async function getWidgetData(widgetKey, selectedFacilities = []) {
    const response = await axiosNextGen.get(`/api/widget-data/${widgetKey}`, {
        params: { mode: 'full', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get counts for multiple widgets in a single request
 * @param {string[]} widgetKeys - Array of widget key identifiers
 * @returns {Promise<Record<string, number>>} Record mapping widget keys to counts
 */
export async function getWidgetCounts(widgetKeys) {
    const response = await axiosNextGen.post('/api/widget-data/counts', {
        widgetKeys
    });
    return response.data.counts;
}

/**
 * Get exams without results data
 * @returns {Promise<{count: number, data: import('../../../types/widgetTypes').ExamWithoutResultsRow[]}>}
 */
export async function getExamsWithoutResults(selectedFacilities = []) {
    return getWidgetData('exams-without-results', selectedFacilities);
}

/**
 * Get count of exams without results
 * @returns {Promise<number>}
 */
export async function getExamsWithoutResultsCount(selectedFacilities = []) {
    return getWidgetCount('exams-without-results', selectedFacilities);
}

/**
 * Get exams without results filtered by date range and optional facilities
 * @param {Object} request
 * @param {string} request.startDate - Start date (YYYY-MM-DD)
 * @param {string} request.endDate - End date (YYYY-MM-DD)
 * @param {string} request.ianaTimezone - Client timezone
 * @param {string[]} [request.selectedFacilities] - Facility IDs to filter by
 * @returns {Promise<Array>}
 */
export async function getExamsWithoutResultsFiltered(request) {
    const params = new URLSearchParams();
    params.append('mode', 'full');
    if (request.startDate) params.append('startDate', request.startDate);
    if (request.endDate) params.append('endDate', request.endDate);
    params.append('ianaTimezone', request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    (request.selectedFacilities || []).forEach(id => params.append('SelectedFacilities', id));

    const response = await axiosNextGen.get(`/api/widget-data/exams-without-results?${params.toString()}`);
    // API may return a plain array or a wrapped { data: [], count: N } object
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
}

/**
 * Get count of unperformed exams
 * @returns {Promise<import('../../../types/unperformedExamsTypes').UnperformedExamsCountResponse>}
 */
export async function getUnperformedExamsCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/unperformed-exams', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get unperformed exams data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/unperformedExamsTypes').UnperformedExamsDataResponse>}
 */
export async function getUnperformedExamsData(page = 1, pageSize = 50, selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/unperformed-exams', {
        params: {
            mode: 'full',
            page,
            pageSize,
            ...(selectedFacilities.length > 0 && { selectedFacilities })
        }
    });
    return response.data;
}

/**
 * Delete an exam with optional grouped exam deletion
 * @param {string} examId - The exam ID (GUID)
 * @param {string} reasonForDeletion - The reason for deletion ID
 * @param {boolean} deleteBothGroupedExams - Whether to delete both grouped exams
 * @returns {Promise<{message: string}>}
 */
export async function deleteExam(examId, reasonForDeletion, deleteBothGroupedExams = false) {
    const response = await axiosNextGen.delete(`/api/exams/${examId}`, {
        data: {
            reasonForDeletion,
            deleteBothGroupedExams
        }
    });
    return response.data;
}

/**
 * Get count of follow-up tracking records
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getFollowUpTrackingCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/follow-up-tracking', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get follow-up tracking data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').FollowUpTrackingDataResponse>}
 */
export async function getFollowUpTrackingData(page = 1, pageSize = 50, search = '', selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/follow-up-tracking', {
        params: {
            mode: 'full',
            page,
            pageSize,
            ...(search?.trim() ? { search } : {}),
            ...(selectedFacilities.length > 0 && { selectedFacilities })
        }
    });
    return response.data;
}

/**
 * Get count-only result for exams without results filtered by date range and optional facilities
 * @param {Object} request
 * @param {string} request.startDate
 * @param {string} request.endDate
 * @param {string} request.ianaTimezone
 * @param {string[]} [request.selectedFacilities]
 * @returns {Promise<{count: number}>}
 */
export async function getExamsWithoutResultsFilteredCount(request) {
    const params = new URLSearchParams();
    params.append('mode', 'count');
    if (request.startDate) params.append('startDate', request.startDate);
    if (request.endDate) params.append('endDate', request.endDate);
    params.append('ianaTimezone', request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    (request.selectedFacilities || []).forEach(id => params.append('SelectedFacilities', id));

    const response = await axiosNextGen.get(`/api/widget-data/exams-without-results?${params.toString()}`);
    return response.data;
}

/**
 * Get count-only result for follow-up tracking filtered records
 * @param {Object} request
 * @param {string} request.startDate
 * @param {string} request.endDate
 * @param {string} request.ianaTimezone
 * @param {string[]} request.selectedFacilities
 * @param {string[]} request.selectedBirads
 * @returns {Promise<{count: number}>}
 */
export async function getFollowUpTrackingFilteredCount(request) {
    const response = await axiosNextGen.post('/api/widget-data/follow-up-tracking', {
        mode: 'count',
        startDate: request.startDate || undefined,
        endDate: request.endDate || undefined,
        ianaTimezone: request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        selectedFacilities: request.selectedFacilities || [],
        selectedBirads: request.selectedBirads || [],
        ...(request.search?.trim() ? { search: request.search } : {})
    });
    return response.data;
}

/**
 * Get follow-up tracking records filtered by date range, facilities, and BIRADS
 * @param {Object} request
 * @param {string} request.startDate - Start date (YYYY-MM-DD)
 * @param {string} request.endDate - End date (YYYY-MM-DD)
 * @param {string} request.ianaTimezone - Client timezone
 * @param {string[]} request.selectedFacilities - Facility IDs
 * @param {string[]} request.selectedBirads - BIRADS values
 * @returns {Promise<Array>}
 */
export async function getFollowUpTrackingFiltered(request) {
    const response = await axiosNextGen.post('/api/widget-data/follow-up-tracking', {
        mode: 'full',
        page: request.page || 1,
        pageSize: request.pageSize || 50,
        startDate: request.startDate || undefined,
        endDate: request.endDate || undefined,
        ianaTimezone: request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        selectedFacilities: request.selectedFacilities || [],
        selectedBirads: request.selectedBirads || [],
        ...(request.search?.trim() ? { search: request.search } : {})
    });
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
}

/**
 * Get count of incomplete exams
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getIncompleteExamsCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/exams-missing-info', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get incomplete exams data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').IncompleteExamsDataResponse>}
 */
export async function getIncompleteExamsData(page = 1, pageSize = 50, selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/exams-missing-info', {
        params: {
            mode: 'full',
            page,
            pageSize,
            ...(selectedFacilities.length > 0 && { selectedFacilities })
        }
    });
    return response.data;
}

/**
 * Get count of result letters not printed
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getResultLetterNotPrintedCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/result-letters-not-printed', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get result letters not printed data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').ResultLetterNotPrintedDataResponse>}
 */
export async function getResultLetterNotPrintedData(page = 1, pageSize = 50, examTypes = [], selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/result-letters-not-printed', {
        params: {
            mode: 'full',
            page,
            pageSize,
            ...(examTypes.length > 0 && { examTypes }),
            ...(selectedFacilities.length > 0 && { selectedFacilities })
        }
    });
    return response.data;
}

/**
 * Get count of missing same-day provider visits
 * @returns {Promise<{widgetKey: string, mode: string, count: number, data: null}>}
 */
export async function getMissingSameDayProviderVisitsCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/missing-same-day-provider-visits', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get missing same-day provider visits data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<import('../../../types/widgetTypes').MissingSameDayProviderVisitsDataResponse>}
 */
export async function getMissingSameDayProviderVisitsData(page = 1, pageSize = 50, selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/missing-same-day-provider-visits', {
        params: {
            mode: 'full',
            page,
            pageSize,
            ...(selectedFacilities.length > 0 && { selectedFacilities })
        }
    });
    return response.data;
}

export async function renderResultLetter(request) {
    const response = await axiosNextGen.post('/api/reports/plain-language-letter', request, {
        responseType: 'blob',
    });
    return response.data;
}

export async function getBlankHxSheetReport(request) {
    const response = await axiosNextGen.post('/api/reports/blank-hx-sheet', request, {
        responseType: 'blob',
    });
    return response.data;
}

/**
 * Get tech summary data for widget display
 * @param {Object} request - Tech summary request parameters (in exact API format)
 * @param {string} request.startDate - Start date (ISO string)
 * @param {string} request.endDate - End date (ISO string)
 * @param {string} request.facilityId - Facility ID (GUID)
 * @param {string[]} request.technologistIds - Array of technologist user IDs
 * @param {string[]} request.examTypeIds - Array of exam type IDs
 * @param {boolean} request.showDetailed - Whether to include exam details
 * @param {string} request.ianaTimezone - Client timezone (e.g., "America/New_York")
 * @returns {Promise<{techName: string, userId: string, totalExams: number, details: Array}[]>}
 */
export async function getTechSummaryData(request) {
    const response = await axiosNextGen.post('/api/reports/tech-summary/data', request);
    return response.data;
}

/**
 * Download tech summary report as PDF
 * @param {Object} request - Tech summary request parameters
 * @returns {Promise<Blob>} PDF blob
 */
export async function downloadTechSummaryReport(request) {
    const formData = new FormData();
    formData.append('startDate', request.startDate || '');
    formData.append('endDate', request.endDate || '');
    formData.append('selectedFacility', request.selectedFacilities?.[0] || '');
    formData.append('selectedTech', request.selectedTechs?.[0] || '');

    if (request.selectedExamTypes && request.selectedExamTypes.length > 0) {
        formData.append('selectedExamTypes', request.selectedExamTypes.join(','));
    }

    formData.append('showDetails', request.showDetails || false);
    formData.append('ianaTimezone', request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);

    const response = await axiosNextGen.post('/api/RenderTechDetailsAndSummary', formData, {
        responseType: 'blob',
        headers: {
            'Content-Type': 'multipart/form-data',
        }
    });

    return response.data;
}

/**
 * Get count of exam perform failures
 */
export async function getExamPerformFailuresCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/exam-perform-failures', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get exam perform failures data with pagination
 * @param {number} page
 * @param {number} pageSize
 */
export async function getExamPerformFailuresData(page = 1, pageSize = 50, selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/exam-perform-failures', {
        params: { mode: 'full', page, pageSize, ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get exam perform failures filtered by date range and facilities with pagination
 * @param {Object} request
 * @param {string} [request.startDate]
 * @param {string} [request.endDate]
 * @param {string} request.ianaTimezone
 * @param {string[]} [request.selectedFacilities]
 * @param {number} [request.page]
 * @param {number} [request.pageSize]
 */
export async function getExamPerformFailuresFiltered(request) {
    const params = new URLSearchParams();
    params.append('mode', 'full');
    params.append('page', request.page || 1);
    params.append('pageSize', request.pageSize || 50);
    if (request.startDate) params.append('startDate', request.startDate);
    if (request.endDate) params.append('endDate', request.endDate);
    params.append('ianaTimezone', request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    (request.selectedFacilities || []).forEach(id => params.append('selectedFacilities', id));
    const response = await axiosNextGen.get(`/api/widget-data/exam-perform-failures?${params.toString()}`);
    return response.data;
}

/**
 * Get count of exam perform failures filtered by date range and facilities
 * @param {Object} request
 */
export async function getExamPerformFailuresFilteredCount(request) {
    const params = new URLSearchParams();
    params.append('mode', 'count');
    if (request.startDate) params.append('startDate', request.startDate);
    if (request.endDate) params.append('endDate', request.endDate);
    params.append('ianaTimezone', request.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    (request.selectedFacilities || []).forEach(id => params.append('selectedFacilities', id));
    const response = await axiosNextGen.get(`/api/widget-data/exam-perform-failures?${params.toString()}`);
    return response.data;
}

/**
 * Get unperformed exams for the reconcile dialog picker (searchable, up to 200)
 * Reuses the existing unperformed-exams widget endpoint with a search param.
 * @param {string} [search]
 * @param {string} [facilityId]
 * @param {number} [page]
 * @param {number} [pageSize]
 */
export async function getUnperformedExamsForReconcile(examPerformFailureId, search = '', page = 1, pageSize = 50) {
    const response = await axiosNextGen.get(`/api/widget-data/exam-perform-failures/${examPerformFailureId}/reconciliation-candidates`, {
        params: {
            page,
            pageSize,
            ...(search?.trim() ? { search } : {})
        }
    });
    return response.data;
}

/**
 * Reconcile an exam perform failure by linking it to an unperformed exam
 * @param {Object} data
 * @param {string} data.examPerformFailureId
 * @param {string} data.patientExamId
 * @param {string|null} data.examTypeId
 * @param {string|null} data.performingTechUserId
 * @param {string|null} data.providerId
 */
export async function reconcileExamPerformFailure(data) {
    const response = await axiosNextGen.post('/api/exam-perform-failures/reconcile', data);
    return response.data;
}

/**
 * Dismiss an exam perform failure without updating any exam data
 * @param {string} id - The examPerformFailureId
 */
export async function dismissExamPerformFailure(id) {
    const response = await axiosNextGen.post(`/api/exam-perform-failures/${id}/dismiss`);
    return response.data;
}

/**
 * Get count of result entry failures
 */
export async function getResultEntryFailuresCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/result-entry-failures', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get result entry failures data with pagination and optional filters
 * @param {number} page
 * @param {number} pageSize
 * @param {Object} [filters]
 * @param {string} [filters.startDate]
 * @param {string} [filters.endDate]
 * @param {string} [filters.ianaTimezone]
 * @param {string[]} [filters.selectedFacilities]
 */
export async function getResultEntryFailuresData(page = 1, pageSize = 50, filters = {}) {
    const params = new URLSearchParams();
    params.append('mode', 'full');
    params.append('page', page);
    params.append('pageSize', pageSize);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    params.append('ianaTimezone', filters.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    (filters.selectedFacilities || []).forEach(id => params.append('selectedFacilities', id));
    const response = await axiosNextGen.get(`/api/widget-data/result-entry-failures?${params.toString()}`);
    return response.data;
}

/**
 * Get reconciliation candidates for a result entry failure
 * @param {string} resultEntryFailureId
 * @param {number} [page]
 * @param {number} [pageSize]
 */
export async function getResultEntryFailureCandidates(resultEntryFailureId, page = 1, pageSize = 50) {
    const response = await axiosNextGen.get(
        `/api/widget-data/result-entry-failures/${resultEntryFailureId}/reconciliation-candidates`,
        { params: { page, pageSize } }
    );
    return response.data;
}

/**
 * Reconcile a result entry failure to an exam
 * @param {Object} data
 * @param {string} data.resultEntryFailureId
 * @param {string} data.patientExamId
 */
export async function reconcileResultEntryFailure(data) {
    const response = await axiosNextGen.post('/api/result-entry-failures/reconcile', data);
    return response.data;
}

/**
 * Dismiss a result entry failure without reconciling
 * @param {string} id - The resultEntryFailureId
 */
export async function dismissResultEntryFailure(id) {
    const response = await axiosNextGen.post(`/api/result-entry-failures/${id}/dismiss`);
    return response.data;
}

/**
 * Get count of print house monitoring results requiring action
 */
export async function getPrintHouseMonitoringCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/print-house-monitoring', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get print house monitoring data with pagination
 * @param {string} [dataset='requiring-action'] - 'requiring-action' or 'pending-print'
 * @param {number} [page=1]
 * @param {number} [pageSize=50]
 */
export async function getPrintHouseMonitoringData(dataset = 'requiring-action', page = 1, pageSize = 50, examTypes = [], selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/print-house-monitoring', {
        params: {
            mode: 'full', dataset, page, pageSize,
            ...(examTypes.length > 0 && { examTypes }),
            ...(selectedFacilities.length > 0 && { selectedFacilities })
        }
    });
    return response.data;
}

export async function dismissPrintHouseMonitoring(patientResultId) {
    const response = await axiosNextGen.post(`/api/widget-data/print-house-monitoring/${patientResultId}/dismiss`);
    return response.data;
}

/**
 * Get count of unprinted recall letters
 */
export async function getUnprintedRecallLettersCount(selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/unprinted-recall-letters', {
        params: { mode: 'count', ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

/**
 * Get unprinted recall letters data with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 */
export async function getUnprintedRecallLettersData(page = 1, pageSize = 50, selectedFacilities = []) {
    const response = await axiosNextGen.get('/api/widget-data/unprinted-recall-letters', {
        params: { mode: 'full', page, pageSize, ...(selectedFacilities.length > 0 && { selectedFacilities }) }
    });
    return response.data;
}

export async function getAllExamTypes() {
    const response = await axiosNextGen.get('/api/examtypes');
    return response.data;
}

export async function dismissUnprintedRecallLetter(patientRecallId) {
    const response = await axiosNextGen.post(`/api/widget-data/unprinted-recall-letters/${patientRecallId}/dismiss`);
    return response.data;
}

export async function renderRecallLetter(patientRecallId) {
    const response = await axiosNextGen.get(`/api/recall/render-letter/${patientRecallId}`, {
        responseType: 'blob'
    });
    return response.data;
}

const widgetDataService = {
    getWidgetCount,
    getWidgetData,
    getWidgetCounts,
    getExamsWithoutResults,
    getExamsWithoutResultsCount,
    getUnperformedExamsCount,
    getUnperformedExamsData,
    deleteExam,
    getFollowUpTrackingCount,
    getFollowUpTrackingData,
    getIncompleteExamsCount,
    getIncompleteExamsData,
    getResultLetterNotPrintedCount,
    getResultLetterNotPrintedData,
    getMissingSameDayProviderVisitsCount,
    getMissingSameDayProviderVisitsData,
    renderResultLetter,
    getTechSummaryData,
    downloadTechSummaryReport,
    streamWidgetCounts,
    getExamPerformFailuresCount,
    getExamPerformFailuresData,
    getExamPerformFailuresFiltered,
    getExamPerformFailuresFilteredCount,
    getUnperformedExamsForReconcile,
    reconcileExamPerformFailure,
    dismissExamPerformFailure,
    getResultEntryFailuresCount,
    getResultEntryFailuresData,
    getResultEntryFailureCandidates,
    reconcileResultEntryFailure,
    dismissResultEntryFailure,
    getPrintHouseMonitoringCount,
    getPrintHouseMonitoringData,
    dismissPrintHouseMonitoring,
    getUnprintedRecallLettersCount,
    getUnprintedRecallLettersData,
    dismissUnprintedRecallLetter,
    renderRecallLetter,
    getBlankHxSheetReport
};

export default widgetDataService;

/**
 * Open an SSE connection to the widget counts stream endpoint.
 * @param {(widgetKey: string, count: number) => void} onCount
 * @param {(widgetKey: string, error: string) => void} onWidgetError
 * @param {() => void} onDone
 * @param {() => void} onStreamError
 * @param {string[]} [selectedFacilities] - Facility IDs to filter counts by
 * @param {string[]} [widgetKeys] - Widget keys to compute counts for; empty = server computes all
 * @returns {() => void} Cleanup function that closes the EventSource
 */
export function streamWidgetCounts(onCount, onWidgetError, onDone, onStreamError, selectedFacilities = [], widgetKeys = []) {
    const token = Globals.nextGenToken;
    if (!token) {
        onStreamError();
        return () => {};
    }

    const baseUrl = (axiosNextGen.defaults.baseURL || '').replace(/\/$/, '');
    const facilityParams = selectedFacilities.length > 0
        ? selectedFacilities.map(id => `&selectedFacilities=${encodeURIComponent(id)}`).join('')
        : '';
    // Scope the stream to the widgets actually displayed - the server skips count queries for the rest
    const widgetKeyParams = widgetKeys.length > 0
        ? widgetKeys.map(key => `&widgetKeys=${encodeURIComponent(key)}`).join('')
        : '';
    const url = `${baseUrl}/api/widget-data/counts/stream?access_token=${encodeURIComponent(token)}${facilityParams}${widgetKeyParams}`;
    const es = new EventSource(url);

    es.addEventListener('widget-count', (e) => {
        try {
            const { WidgetKey, widgetKey, Count, count } = JSON.parse(e.data);
            const key = WidgetKey ?? widgetKey;
            if (key != null) onCount(key, Count ?? count);
        } catch (err) {
            console.error('Failed to parse widget-count event:', err);
        }
    });

    es.addEventListener('widget-error', (e) => {
        try {
            const { WidgetKey, widgetKey, Error: Err, error } = JSON.parse(e.data);
            onWidgetError(WidgetKey ?? widgetKey, Err ?? error ?? 'Unknown error');
        } catch (err) {
            console.error('Failed to parse widget-error event:', err);
        }
    });

    es.addEventListener('done', () => { es.close(); onDone(); });
    es.onerror = () => { es.close(); onStreamError(); };

    return () => es.close();
}
